/**
 * renderInlineBold — the one place a HITL question's free-text prompt
 * (``pending_action`` / ``question`` / ``message``) gets ``**bold**``
 * markdown turned into an actual ``<strong>``.
 *
 * Several question builders already emit ``**bold**`` around the specific
 * thing they want the AM to notice — e.g. oversight.py's
 * ``f"About to run **{_step_label}** ({risk} risk)."`` — but until this
 * helper existed nothing on either surface (ora / the Outlook add-in) ever
 * parsed it: the prompt paragraph rendered the raw string, so the AM saw
 * literal asterisks instead of emphasis. Both HitlChoicePanel (the
 * ``review_checkpoint`` / actions-bearing dispatch) and HitlQuestionPanel's
 * generic Approve/Reject fallback (oversight_level_review and any other
 * schema/choice-less gate) render their one prompt line through this so
 * every gate benefits at once — no per-gate frontend change needed as new
 * question builders adopt ``**bold**``.
 *
 * Deliberately minimal: bold is the only markdown construct supported.
 * No italics, links, or nested spans — HITL prompts are short instructional
 * sentences, not rich documents, and a fuller markdown parser would be a
 * bigger dependency for a need that doesn't exist yet.
 *
 * A ``**`` left UNPAIRED after pairing is dropped rather than shown: the
 * waiting queue's ``waiting_for`` is truncated server-side at 200 chars (plus
 * "…"), which can cut a pair in half. Lone single ``*`` stay literal.
 *
 * ``stripInlineBold`` is the same parse flattened to plain text, for
 * aria-labels and other string-only contexts (a screen reader would
 * otherwise read the markers aloud).
 */
import type { ReactNode } from "react";

const BOLD_SPLIT = /(\*\*[^*]+\*\*)/g;
const MARKER = /\*\*/g;

/** `split` with one capture group puts the paired runs at the odd indices. */
function segments(text: string): { text: string; bold: boolean }[] {
  return text.split(BOLD_SPLIT).map((part, i) =>
    i % 2 === 1
      ? { text: part.slice(2, -2), bold: true }
      : { text: part.replace(MARKER, ""), bold: false },
  );
}

export function renderInlineBold(text: string): ReactNode {
  if (!text.includes("**")) return text; // no bold markers — skip the array wrap
  return segments(text).map((s, i) =>
    s.bold ? <strong key={i}>{s.text}</strong> : s.text,
  );
}

export function stripInlineBold(text: string): string {
  if (!text.includes("**")) return text;
  return segments(text)
    .map((s) => s.text)
    .join("");
}

export default renderInlineBold;
